import { ALPHA_STOPS, BURST_MAX, COLOUR_STOPS, FLICKER_HZ_RANGE, GRAVITY_RANGE, LIFE_RANGE_MS, LIGHT_INTENSITY_RANGE, LIGHT_RADIUS_RANGE, MAX_PARTICLES_PER_EMITTER, MAX_PRESET_ID, MAX_SIZE_ENTRIES, MAX_SUB_KINDS, RATE_RANGE, SIZE_RANGE_PX, SPEED_RANGE, STREAK_RANGE, SWAY_AMPLITUDE_RANGE, SWAY_HZ_RANGE } from "./limits.ts";
import type { EffectPreset, ParticlePreset } from "./types.ts";

/**
 * One thing wrong with a preset: the field path and what is wrong with it.
 */
export interface EffectProblem {
  readonly field: string;
  readonly message: string;
}

/**
 * The verdict on a preset.
 */
export type EffectVerdict = { readonly ok: true; readonly preset: EffectPreset } | { readonly ok: false; readonly problems: readonly EffectProblem[] };

const PRESET_ID = new RegExp(`^[a-z0-9-]{1,${MAX_PRESET_ID}}$`);
const COMMON_FIELDS = ["id", "name", "group", "words", "kind"];
const PARTICLE_FIELDS = [...COMMON_FIELDS, "rate", "burst", "kinds"];
const LIGHT_FIELDS = [...COMMON_FIELDS, "radius", "rgb", "intensity", "falloff", "flicker"];
const SUB_KIND_FIELDS = ["share", "life", "size", "speed", "direction", "spread", "gravity", "streak", "sway", "colours", "alpha", "blend", "emissive"];
const SHARE_TOLERANCE = 1e-6;

/**
 * Whether a value is a plain object.
 *
 * @param value Anything.
 * @returns True for a non-null, non-array object.
 */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Adds a problem when a value is not a finite number inside a range.
 *
 * @param problems The list to add to.
 * @param field The field path.
 * @param value The value.
 * @param range Its inclusive range.
 * @param whole Whether it must be a whole number.
 */
function checkNumber(problems: EffectProblem[], field: string, value: unknown, range: { readonly min: number; readonly max: number }, whole = false): void {
  if (typeof value !== "number" || !Number.isFinite(value) || value < range.min || value > range.max || (whole && !Number.isInteger(value))) {
    problems.push({ field, message: `must be ${whole ? "a whole number" : "a number"} from ${range.min} to ${range.max}` });
  }
}

/**
 * Adds a problem when a value is not a [min, max] pair inside a range with min not above max.
 *
 * @param problems The list to add to.
 * @param field The field path.
 * @param value The value.
 * @param range The inclusive range both ends lie in.
 */
function checkPair(problems: EffectProblem[], field: string, value: unknown, range: { readonly min: number; readonly max: number }): void {
  const ok = Array.isArray(value) && value.length === 2 && value.every((n) => typeof n === "number" && Number.isFinite(n) && n >= range.min && n <= range.max) && (value[0] as number) <= (value[1] as number);
  if (!ok) problems.push({ field, message: `must be [min, max] within ${range.min} to ${range.max}, min not above max` });
}

/**
 * Adds a problem for each field of an object that is not in the allowed list.
 *
 * @param problems The list to add to.
 * @param prefix The path prefix, empty for the preset itself.
 * @param record The object.
 * @param allowed The field names it may have.
 */
function checkFields(problems: EffectProblem[], prefix: string, record: Record<string, unknown>, allowed: readonly string[]): void {
  for (const key of Object.keys(record)) {
    if (!allowed.includes(key)) problems.push({ field: `${prefix}${key}`, message: "is not a field here" });
  }
}

/**
 * Checks a ramp: stop count, stops ascending, first at 0 and last at 1, and each stop's value.
 *
 * @param problems The list to add to.
 * @param field The ramp's field path.
 * @param value The ramp.
 * @param count The allowed stop count.
 * @param stopValue Checks one stop's own value, adding its problems.
 */
function checkRamp(problems: EffectProblem[], field: string, value: unknown, count: { readonly min: number; readonly max: number }, stopValue: (stop: Record<string, unknown>, path: string) => void): void {
  if (!Array.isArray(value) || value.length < count.min || value.length > count.max) {
    problems.push({ field, message: `must list ${count.min} to ${count.max} stops` });
    return;
  }
  let previous = -1;
  let ordered = true;
  value.forEach((stop, index) => {
    if (!isRecord(stop)) {
      problems.push({ field: `${field}[${index}]`, message: "must be an object" });
      ordered = false;
      return;
    }
    const at = stop.at;
    if (typeof at !== "number" || !(at >= 0 && at <= 1) || at <= previous) ordered = false;
    else previous = at;
    stopValue(stop, `${field}[${index}]`);
  });
  const first = (value[0] as Record<string, unknown> | undefined)?.at;
  const last = (value[value.length - 1] as Record<string, unknown> | undefined)?.at;
  if (!ordered || first !== 0 || last !== 1) problems.push({ field, message: "stops must rise strictly from at 0 to at 1" });
}

/**
 * Checks one sub-kind of a particle preset.
 *
 * @param problems The list to add to.
 * @param path The sub-kind's field path.
 * @param value The sub-kind.
 */
function checkSubKind(problems: EffectProblem[], path: string, value: unknown): void {
  if (!isRecord(value)) {
    problems.push({ field: path, message: "must be an object" });
    return;
  }
  checkFields(problems, `${path}.`, value, SUB_KIND_FIELDS);
  checkNumber(problems, `${path}.share`, value.share, { min: 0.01, max: 1 });
  checkPair(problems, `${path}.life`, value.life, LIFE_RANGE_MS);
  const size = value.size;
  if (!Array.isArray(size) || size.length < 1 || size.length > MAX_SIZE_ENTRIES) {
    problems.push({ field: `${path}.size`, message: `must list 1 to ${MAX_SIZE_ENTRIES} sizes` });
  } else {
    size.forEach((entry, index) => {
      if (!isRecord(entry)) {
        problems.push({ field: `${path}.size[${index}]`, message: "must be an object" });
        return;
      }
      checkNumber(problems, `${path}.size[${index}].px`, entry.px, SIZE_RANGE_PX, true);
      checkNumber(problems, `${path}.size[${index}].weight`, entry.weight, { min: 1, max: 100 });
    });
  }
  checkPair(problems, `${path}.speed`, value.speed, SPEED_RANGE);
  checkNumber(problems, `${path}.direction`, value.direction, { min: 0, max: 360 });
  checkNumber(problems, `${path}.spread`, value.spread, { min: 0, max: 360 });
  checkNumber(problems, `${path}.gravity`, value.gravity, GRAVITY_RANGE);
  if (value.streak !== undefined) checkNumber(problems, `${path}.streak`, value.streak, STREAK_RANGE);
  if (!isRecord(value.sway)) {
    problems.push({ field: `${path}.sway`, message: "must be an object with amplitude and hz" });
  } else {
    checkNumber(problems, `${path}.sway.amplitude`, value.sway.amplitude, SWAY_AMPLITUDE_RANGE);
    checkNumber(problems, `${path}.sway.hz`, value.sway.hz, SWAY_HZ_RANGE);
  }
  checkRamp(problems, `${path}.colours`, value.colours, COLOUR_STOPS, (stop, stopPath) => checkNumber(problems, `${stopPath}.rgb`, stop.rgb, { min: 0, max: 0xffffff }, true));
  checkRamp(problems, `${path}.alpha`, value.alpha, ALPHA_STOPS, (stop, stopPath) => checkNumber(problems, `${stopPath}.value`, stop.value, { min: 0, max: 1 }));
  if (value.blend !== "normal" && value.blend !== "add") problems.push({ field: `${path}.blend`, message: "must be normal or add" });
  if (typeof value.emissive !== "boolean") problems.push({ field: `${path}.emissive`, message: "must be true or false" });
}

/**
 * How many particles a preset keeps alive at once in steady state: each sub-kind's share of the rate times its longest life,
 * plus the burst.
 *
 * @param preset A particle preset with valid fields.
 * @returns The steady count, not rounded.
 */
export function steadyCount(preset: ParticlePreset): number {
  let count = preset.burst;
  for (const kind of preset.kinds) count += (preset.rate * kind.share * kind.life[1]) / 1000;
  return count;
}

/**
 * The steady count of a preset as parsed, read defensively so it can be judged beside other problems.
 *
 * @param rate The preset's `rate`.
 * @param burst The preset's `burst`.
 * @param kinds The preset's `kinds`.
 * @returns The count, or null when a number it needs is not a finite number.
 */
function steadyCountOf(rate: unknown, burst: unknown, kinds: readonly unknown[]): number | null {
  if (typeof rate !== "number" || typeof burst !== "number" || !Number.isFinite(rate) || !Number.isFinite(burst)) return null;
  let count = burst;
  for (const kind of kinds) {
    if (!isRecord(kind) || typeof kind.share !== "number" || !Array.isArray(kind.life)) return null;
    const longest = kind.life[1];
    if (typeof longest !== "number" || !Number.isFinite(longest)) return null;
    count += (rate * kind.share * longest) / 1000;
  }
  return count;
}

/**
 * Checks the fields of a particle preset.
 *
 * @param problems The list to add to.
 * @param record The preset as parsed.
 */
function checkParticles(problems: EffectProblem[], record: Record<string, unknown>): void {
  checkFields(problems, "", record, PARTICLE_FIELDS);
  checkNumber(problems, "rate", record.rate, RATE_RANGE);
  checkNumber(problems, "burst", record.burst, { min: 0, max: BURST_MAX }, true);
  const kinds = record.kinds;
  if (!Array.isArray(kinds) || kinds.length < 1 || kinds.length > MAX_SUB_KINDS) {
    problems.push({ field: "kinds", message: `must list 1 to ${MAX_SUB_KINDS} sub-kinds` });
    return;
  }
  kinds.forEach((kind, index) => checkSubKind(problems, `kinds[${index}]`, kind));
  const total = kinds.reduce((sum: number, kind) => sum + (isRecord(kind) && typeof kind.share === "number" ? kind.share : 0), 0);
  if (Math.abs(total - 1) > SHARE_TOLERANCE) problems.push({ field: "kinds", message: "shares must sum to 1" });
  const steady = steadyCountOf(record.rate, record.burst, kinds);
  if (steady !== null && steady > MAX_PARTICLES_PER_EMITTER) {
    problems.push({ field: "rate", message: `rate times life plus burst keeps over ${MAX_PARTICLES_PER_EMITTER} particles alive` });
  }
}

/**
 * Checks the fields of a light preset.
 *
 * @param problems The list to add to.
 * @param record The preset as parsed.
 */
function checkLight(problems: EffectProblem[], record: Record<string, unknown>): void {
  checkFields(problems, "", record, LIGHT_FIELDS);
  checkNumber(problems, "radius", record.radius, LIGHT_RADIUS_RANGE);
  checkNumber(problems, "rgb", record.rgb, { min: 0, max: 0xffffff }, true);
  checkNumber(problems, "intensity", record.intensity, LIGHT_INTENSITY_RANGE);
  if (record.falloff !== "smooth" && record.falloff !== "linear" && record.falloff !== "hard") problems.push({ field: "falloff", message: "must be smooth, linear or hard" });
  if (!isRecord(record.flicker)) {
    problems.push({ field: "flicker", message: "must be an object with amount and hz" });
  } else {
    checkNumber(problems, "flicker.amount", record.flicker.amount, { min: 0, max: 1 });
    checkNumber(problems, "flicker.hz", record.flicker.hz, FLICKER_HZ_RANGE);
  }
}

/**
 * Checks an effect preset and names every problem. It runs on save, at compile and when a client reads the manifest, so a
 * hand-edited or stale preset cannot flood a client (ADR-141 decision A2).
 *
 * @param value The preset as parsed from JSON.
 * @returns The preset when it is sound, else every problem with its field.
 * @example
 * validateEffectPreset(JSON.parse(text));
 */
export function validateEffectPreset(value: unknown): EffectVerdict {
  const problems: EffectProblem[] = [];
  if (!isRecord(value)) return { ok: false, problems: [{ field: "", message: "a preset must be an object" }] };
  if (value.kind !== "particles" && value.kind !== "light") return { ok: false, problems: [{ field: "kind", message: "must be particles or light" }] };
  if (typeof value.id !== "string" || !PRESET_ID.test(value.id)) problems.push({ field: "id", message: `must be 1 to ${MAX_PRESET_ID} lower-case letters, digits or hyphens` });
  if (typeof value.name !== "string" || value.name.trim() === "" || value.name.length > 80) problems.push({ field: "name", message: "must be 1 to 80 characters" });
  if (typeof value.group !== "string" || value.group.length > 40) problems.push({ field: "group", message: "must be text of at most 40 characters" });
  if (!Array.isArray(value.words) || value.words.length > 16 || value.words.some((w) => typeof w !== "string")) problems.push({ field: "words", message: "must list at most 16 words" });
  if (value.kind === "particles") checkParticles(problems, value);
  else checkLight(problems, value);
  return problems.length === 0 ? { ok: true, preset: value as unknown as EffectPreset } : { ok: false, problems };
}
